import { useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'

import { cuenta, errorDelCampo, MINIMO_DE_CONTRASENA } from '../api/cuenta'
import { ErrorDeLaApi } from '../api/nucleo'
import { useSesion } from '../api/sesion'
import { Aviso, Boton, Campo, Tarjeta, Titulo } from '../componentes/ui'
import { campoPorNombre, useEsquema } from '../motor/esquema'

/**
 * Mi cuenta: mi nombre, mi correo y mi contraseña. Cambiar el correo o la contraseña pide la
 * contraseña de ahora —son la llave de la cuenta— y lo comprueba el núcleo, que cuenta los
 * intentos igual que la pantalla de entrar. Se llega desde el menú del usuario.
 */
type Estado = { fallo: unknown; hecho: null | string; enviando: boolean }
const INICIAL: Estado = { fallo: null, hecho: null, enviando: false }

const general = (fallo: unknown, campos: string[]): null | string => {
  if (!fallo) return null
  if (campos.some((c) => errorDelCampo(fallo, c))) return null
  return fallo instanceof ErrorDeLaApi ? fallo.message : 'No hemos podido conectar. Comprueba tu conexión y vuelve a intentarlo.'
}

const Seccion = ({ titulo, descripcion, children }: { children: ReactNode; descripcion: string; titulo: string }) => (
  <Tarjeta className="grid gap-5 md:grid-cols-[14rem_minmax(0,1fr)]">
    <div>
      <h2 className="font-display text-base font-semibold">{titulo}</h2>
      <p className="mt-1 text-[13px] text-tenue">{descripcion}</p>
    </div>
    <div className="flex max-w-md flex-col gap-4">{children}</div>
  </Tarjeta>
)

export const MiCuenta = () => {
  const { usuario } = useSesion()
  const clienteDeConsultas = useQueryClient()
  const { data: esquema } = useEsquema('usuarios')
  const [nombre, setNombre] = useState(usuario?.nombre ?? '')
  const [email, setEmail] = useState('')
  const [actualCorreo, setActualCorreo] = useState('')
  const [actual, setActual] = useState('')
  const [nueva, setNueva] = useState('')
  const [repetida, setRepetida] = useState('')
  const [deNombre, setDeNombre] = useState<Estado>(INICIAL)
  const [deCorreo, setDeCorreo] = useState<Estado>(INICIAL)
  const [deClave, setDeClave] = useState<Estado>(INICIAL)

  if (!usuario) return null
  const rol = campoPorNombre(esquema?.campos ?? [], 'rol')?.opciones?.find((o) => o.valor === usuario.rol)
  const releerSesion = () => clienteDeConsultas.invalidateQueries({ queryKey: ['sesion'] })

  const guardarNombre = async (e: FormEvent) => {
    e.preventDefault()
    setDeNombre({ ...INICIAL, enviando: true })
    try {
      await cuenta.cambiarNombre(usuario.id, nombre.trim())
      await releerSesion()
      setDeNombre({ ...INICIAL, hecho: 'Guardado.' })
    } catch (fallo) {
      setDeNombre({ ...INICIAL, fallo })
    }
  }

  const guardarCorreo = async (e: FormEvent) => {
    e.preventDefault()
    setDeCorreo({ ...INICIAL, enviando: true })
    try {
      const r = await cuenta.cambiarCredenciales({ actual: actualCorreo, email: email.trim() })
      await releerSesion()
      setEmail('')
      setActualCorreo('')
      setDeCorreo({ ...INICIAL, hecho: r.mensaje })
    } catch (fallo) {
      setDeCorreo({ ...INICIAL, fallo })
    }
  }

  const noCoinciden = repetida.length > 0 && nueva !== repetida
  const guardarClave = async (e: FormEvent) => {
    e.preventDefault()
    if (noCoinciden) return
    setDeClave({ ...INICIAL, enviando: true })
    try {
      const r = await cuenta.cambiarCredenciales({ actual, nueva })
      setActual('')
      setNueva('')
      setRepetida('')
      setDeClave({ ...INICIAL, hecho: r.mensaje })
    } catch (fallo) {
      setDeClave({ ...INICIAL, fallo })
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <Titulo descripcion="Tu nombre, tu correo y tu contraseña. Solo los ves y los cambias tú.">Mi cuenta</Titulo>

      <Seccion descripcion="Cómo te ven los demás en la auditoría y en los avisos." titulo="Tus datos">
        <form className="flex flex-col gap-4" onSubmit={guardarNombre}>
          <Campo autoComplete="name" error={errorDelCampo(deNombre.fallo, 'nombre')} etiqueta="Nombre" onChange={(e) => setNombre(e.target.value)} value={nombre} />
          <div className="text-sm">
            <p className="text-[13px] font-medium">Tu papel</p>
            <p className="mt-1 text-tinta-2">
              {rol?.etiqueta ?? usuario.rol}
              {rol?.descripcion ? ` · ${rol.descripcion}` : ''}
            </p>
          </div>
          {general(deNombre.fallo, ['nombre']) && <Aviso tono="error">{general(deNombre.fallo, ['nombre'])}</Aviso>}
          {deNombre.hecho && <Aviso tono="ok">{deNombre.hecho}</Aviso>}
          <div>
            <Boton cargando={deNombre.enviando} disabled={nombre.trim() === (usuario.nombre ?? '')} tipo="primario" type="submit">
              Guardar el nombre
            </Boton>
          </div>
        </form>
      </Seccion>

      <Seccion descripcion={`Ahora entras con ${usuario.email}. Si lo cambias, entrarás con el nuevo.`} titulo="Correo">
        <form className="flex flex-col gap-4" onSubmit={guardarCorreo}>
          <Campo autoComplete="email" error={errorDelCampo(deCorreo.fallo, 'email')} etiqueta="Correo nuevo" onChange={(e) => setEmail(e.target.value)} required type="email" value={email} />
          <Campo
            autoComplete="current-password"
            error={errorDelCampo(deCorreo.fallo, 'actual')}
            etiqueta="Tu contraseña de ahora"
            onChange={(e) => setActualCorreo(e.target.value)}
            required
            type="password"
            value={actualCorreo}
          />
          {general(deCorreo.fallo, ['email', 'actual']) && <Aviso tono="error">{general(deCorreo.fallo, ['email', 'actual'])}</Aviso>}
          {deCorreo.hecho && <Aviso tono="ok">{deCorreo.hecho}</Aviso>}
          <div>
            <Boton cargando={deCorreo.enviando} tipo="primario" type="submit">
              Cambiar el correo
            </Boton>
          </div>
        </form>
      </Seccion>

      <Seccion descripcion="Al cambiarla, se cierra la sesión en los demás dispositivos; en este sigues dentro." titulo="Contraseña">
        <form className="flex flex-col gap-4" onSubmit={guardarClave}>
          <Campo autoComplete="current-password" error={errorDelCampo(deClave.fallo, 'actual')} etiqueta="Tu contraseña de ahora" onChange={(e) => setActual(e.target.value)} required type="password" value={actual} />
          <Campo
            autoComplete="new-password"
            ayuda={`Al menos ${MINIMO_DE_CONTRASENA} caracteres.`}
            error={errorDelCampo(deClave.fallo, 'nueva')}
            etiqueta="Contraseña nueva"
            minLength={MINIMO_DE_CONTRASENA}
            onChange={(e) => setNueva(e.target.value)}
            required
            type="password"
            value={nueva}
          />
          <Campo autoComplete="new-password" error={noCoinciden ? 'No coincide con la de arriba.' : undefined} etiqueta="Repítela" onChange={(e) => setRepetida(e.target.value)} required type="password" value={repetida} />
          {general(deClave.fallo, ['actual', 'nueva']) && <Aviso tono="error">{general(deClave.fallo, ['actual', 'nueva'])}</Aviso>}
          {deClave.hecho && <Aviso tono="ok">{deClave.hecho}</Aviso>}
          <div>
            <Boton cargando={deClave.enviando} disabled={noCoinciden} tipo="primario" type="submit">
              Cambiar la contraseña
            </Boton>
          </div>
        </form>
      </Seccion>
    </div>
  )
}
